import type { ClientJournalTemplate, PosPeriodTotals, TemplateLine, TemplateLineSide, TemplateLineSource } from '../types';

/**
 * The client's journal template, as the screens use it (docs/client-journal-template.md §A). The server is the authority
 * on its rules (`JournalTemplateRules`); this is the default template, the migration of a draft typed on the default
 * boxes, and the figures to and from the form.
 */

/**
 * The seven default boxes (`DefaultJournalTemplate.Lines` on the server), in their order, with core's registry picks.
 * The allowance's source is the sales tax return (core, 2026-10-29): a client that starts its own template from these
 * gets it as a return line. The default POS screen still offers its box, as it always has.
 */
export const DEFAULT_TEMPLATE_LINES: readonly TemplateLine[] = [
  { key: 'salesRevenue', side: 'credit', source: 'pos', registryField: ['sales.net'] },
  { key: 'salesTaxPayable', side: 'credit', source: 'pos', registryField: ['tax.collected', 'tax.surtax'] },
  { key: 'tipsPayable', side: 'credit', source: 'pos', registryField: ['tips.collected'] },
  { key: 'creditCardClearing', side: 'debit', source: 'pos', registryField: ['tender.card'] },
  { key: 'cashOnHand', side: 'debit', source: 'pos', registryField: ['tender.cash'] },
  { key: 'merchantFees', side: 'debit', source: 'pos', registryField: ['fees.merchant'] },
  {
    key: 'collectionAllowanceIncome', side: 'credit', source: 'salesTaxReturn', offsetKey: 'salesTaxPayable',
    registryField: ['return.dr15.collectionAllowance'],
  },
];

export const TEMPLATE_SOURCES: readonly TemplateLineSource[] = ['pos', 'salesTaxReturn', 'bank', 'manual'];

/** The default box each template key took its figure from (`DefaultJournalTemplate.PosFigureByKey`). */
const POS_FIELD_BY_KEY: Readonly<Record<string, keyof PosPeriodTotals>> = {
  salesRevenue: 'netSales',
  salesTaxPayable: 'salesTax',
  tipsPayable: 'tips',
  creditCardClearing: 'cardSettlement',
  cashOnHand: 'cash',
  merchantFees: 'merchantFees',
  collectionAllowanceIncome: 'collectionAllowance',
};

/** The client's own lines, or null when it is on the default template. */
export const ownTemplate = (template: ClientJournalTemplate | null | undefined): TemplateLine[] | null =>
  template?.lines?.length ? template.lines : null;

/** A box the POS journal takes a figure for. 🔴 Never a sales-tax-return line: it is filled from the DR-15 leg. */
export const typedOnPosJournal = (line: TemplateLine): boolean => line.source !== 'salesTaxReturn';

/**
 * What a line is called: its own label, else the client's label for its key, else the standard role's name
 * (`translateRole`, the G/L Mappings screen's words).
 */
export function lineLabel(
  line: TemplateLine,
  rowLabels: Readonly<Record<string, string>> | undefined,
  translateRole: (key: string) => string,
): string {
  return line.label?.trim() || rowLabels?.[line.key]?.trim() || translateRole(line.key);
}

export type LineForm = Record<string, string>;

/** The form's boxes from saved figures: a null figure is a blank box, never "0". */
export function lineFormFrom(lines: readonly TemplateLine[], figures: Readonly<Record<string, number | null>> | null | undefined): LineForm {
  return Object.fromEntries(lines.map((l) => [l.key, figures?.[l.key] == null ? '' : String(figures[l.key])]));
}

/**
 * The figures to save from the boxes, null when every box is blank. Figures the form does not show (a migrated
 * allowance on a return line) are KEPT, so nothing a bookkeeper typed is dropped by a save.
 */
export function lineFiguresOf(
  form: LineForm,
  parse: (raw: string) => number | null,
  carried: Readonly<Record<string, number | null>> = {},
): Record<string, number | null> | null {
  const figures: Record<string, number | null> = { ...carried };
  for (const [key, raw] of Object.entries(form)) figures[key] = parse(raw);
  return Object.values(figures).some((v) => v != null) ? figures : null;
}

/**
 * A draft saved on the default boxes (`posFigures`), opened on a client with its own template (§A7). Each box goes to the
 * line with the same key. A box with no such line (net sales, on a template whose sales are split) is not placed: it
 * stays in `posFigures`, untouched, and is the PRIOR TOTAL the sales check compares the lines with (§A9). Nothing is
 * written by opening it, and the next save leaves `posFigures` exactly as it was.
 */
export function migratePosFigures(posFigures: PosPeriodTotals | null | undefined, lines: readonly TemplateLine[]): Record<string, number | null> {
  const figures: Record<string, number | null> = {};
  if (!posFigures) return figures;
  const keys = new Set(lines.map((l) => l.key));
  for (const [key, field] of Object.entries(POS_FIELD_BY_KEY)) {
    const value = posFigures[field];
    if (value != null && keys.has(key)) figures[key] = value;
  }
  return figures;
}

/** Roles that are never sales, whatever their side: the tax, the tips, the allowance. */
const NOT_SALES: ReadonlySet<string> = new Set(['salesTaxPayable', 'tipsPayable', 'collectionAllowanceIncome']);

/**
 * A line the sales total counts (§A9): a typed CREDIT line that is sales. A line with registry picks is sales when every
 * pick is a `sales.*` code (core's §3.2: `sales.net`, `sales.food`, `sales.surcharge`, …); a line without picks is sales
 * unless it is the tax, the tips or the allowance.
 */
export function isSalesLine(line: TemplateLine): boolean {
  if (line.side !== 'credit' || !typedOnPosJournal(line)) return false;
  const picks = (line.registryField ?? []).map((f) => f.trim()).filter(Boolean);
  return picks.length > 0 ? picks.every((f) => f.startsWith('sales.')) : !NOT_SALES.has(line.key);
}

const cents = (n: number) => Math.round(n * 100);

/** The sum of the sales lines' figures, to the cent; null when no sales line has a figure. */
export function salesTotal(lines: readonly TemplateLine[], figures: Readonly<Record<string, number | null>> | null | undefined): number | null {
  let sum = 0;
  let any = false;
  for (const line of lines) {
    const value = isSalesLine(line) ? figures?.[line.key] : null;
    if (value == null) continue;
    sum += cents(value);
    any = true;
  }
  return any ? sum / 100 : null;
}

/** A saved journal's sales total: its lines' figures on a template, else its Net sales box. */
export function journalSalesTotal(
  journal: { lineFigures?: Record<string, number | null> | null; posFigures?: PosPeriodTotals | null },
  lines: readonly TemplateLine[] | null,
): number | null {
  if (journal.lineFigures && lines) return salesTotal(lines, journal.lineFigures);
  return journal.posFigures?.netSales ?? null;
}

/** `now − then`, to the cent; null when either is unknown. */
export function difference(now: number | null, then: number | null): number | null {
  return now == null || then == null ? null : (cents(now) - cents(then)) / 100;
}

/** The template a client gets when it starts its own from the default: the seven lines, then the new one (§A8). */
export function startOwnTemplate(current: ClientJournalTemplate | null | undefined, added: TemplateLine): ClientJournalTemplate {
  const base = ownTemplate(current) ?? DEFAULT_TEMPLATE_LINES.map((l) => ({ ...l }));
  return { lines: [...base.filter((l) => l.key !== added.key), added] };
}

export const MAX_HINT = 300;

/** Xero account types whose balance is a credit (wave 4 §2): revenue, liabilities and equity. Everything else is a debit. */
const CREDIT_TYPES: ReadonlySet<string> = new Set(['REVENUE', 'SALES', 'OTHERINCOME', 'CURRLIAB', 'LIABILITY', 'TERMLIAB', 'EQUITY']);

/** The side a line on an account of this type posts to; null when the account has no type (the side is left as it is). */
export function sideForAccountType(type: string | null | undefined): TemplateLineSide | null {
  const t = (type ?? '').trim().toUpperCase();
  if (!t) return null;
  return CREDIT_TYPES.has(t) ? 'credit' : 'debit';
}

/** Roles only the DR-15 leg fills (the salesTaxDr15 template's, not the POS journal's). */
export const DR15_ONLY_ROLES: ReadonlySet<string> = new Set(['collectionAllowanceIncome', 'penaltyExpense', 'interestExpense']);

/**
 * Who fills a line, once its account is picked (wave 4 §2): the sales tax return for a DR-15-only role, else the POS report.
 * A line set to Bank or Typed was set on purpose and keeps it.
 */
export function sourceForKey(key: string, current: TemplateLineSource): TemplateLineSource {
  if (current === 'bank' || current === 'manual') return current;
  return DR15_ONLY_ROLES.has(key) ? 'salesTaxReturn' : 'pos';
}
